/**
 * Inteligência de esquema terapêutico (regimen intelligence).
 *
 * Analisa a lista de tratamentos de um paciente (patient_medications + substances
 * + substance_formulations) e produz leitura clínica estruturada:
 *  - classes farmacológicas presentes e duplicidade terapêutica
 *  - cargas farmacodinâmicas somadas (sedativa, serotoninérgica, catecolaminérgica)
 *  - lacunas de dados que impedem análise PK/PD confiável
 *  - agenda diária (manhã / tarde / noite / sem horário)
 *  - completude 0–100 do esquema
 *
 * Não é concentração plasmática, não substitui bula nem julgamento clínico.
 */

export type RegimenSeverity = "info" | "monitorar" | "cautela" | "relevante" | "critico";

export type PharmClass =
  | "estimulante"
  | "benzodiazepinico"
  | "opioide"
  | "isrs"
  | "irsn"
  | "moduladorSerotoninergico"
  | "noradrenergico"
  | "alfa2agonista"
  | "antipsicoticoSedativo"
  | "antipsicotico"
  | "estabilizador"
  | "dopaminergico"
  | "hipnotico"
  | "gabapentinoide"
  | "outro";

export interface RegimenMedicationInput {
  id: string;
  display_name?: string | null;
  brand_name?: string | null;
  free_text_name?: string | null;
  substance_id?: string | null;
  formulation_id?: string | null;
  current_dose?: number | string | null;
  dose_unit?: string | null;
  frequency?: string | null;
  usual_time?: string | null;
  status?: string | null;
  clinical_rationale?: string | null;
  monitoring_plan?: string | null;
  substances?: { name?: string | null; pharmacological_class?: string | null; clinical_category?: string | null } | null;
  substance_formulations?: { formulation_name?: string | null; curve_model?: string | null; route?: string | null } | null;
}

export interface RegimenItem {
  id: string;
  label: string;
  canonicalName: string | null;
  pharmClass: PharmClass;
  status: string;
  isActive: boolean;
  dose: number | null;
  doseUnit: string | null;
  frequency: string | null;
  timeBucket: "manha" | "tarde" | "noite" | "sem-horario";
  usualTime: string | null;
  gaps: string[];
}

export interface RegimenFinding {
  id: string;
  title: string;
  severity: RegimenSeverity;
  involved: string[];
  interpretation: string;
  action?: string;
  monitor?: string[];
}

export interface RegimenLoad {
  axis: "sedativa" | "serotoninergica" | "catecolaminergica" | "anticolinergica-cognitiva";
  score0_100: number;
  contributors: string[];
}

export interface RegimenIntelligence {
  items: RegimenItem[];
  activeItems: RegimenItem[];
  findings: RegimenFinding[];
  loads: RegimenLoad[];
  schedule: Record<RegimenItem["timeBucket"], RegimenItem[]>;
  completeness0_100: number;
  dataGaps: string[];
}

function norm(value: any): string {
  return String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

const CLASS_PATTERNS: [PharmClass, RegExp][] = [
  ["estimulante", /lisdex|lyberdia|venvanse|vyvanse|elvanse|juneve|anfetam|metilfen|ritalina|concerta|dextroanfet|modafin/],
  ["benzodiazepinico", /clonaz|rivotril|alpraz|diazep|loraz|midaz|bromaz|benzodiazep/],
  ["opioide", /opioid|morfin|oxicodon|codein|fentan|metadon|tramadol|buprenorf/],
  ["hipnotico", /zolpid|zopiclon|eszopiclon|lorazepam sublingual|hipnotic/],
  ["moduladorSerotoninergico", /vortiox|brintellix|trintellix|vurtuoso|mirtazap|trazodon/],
  ["isrs", /sertralin|escitalop|citalop|fluoxet|paroxet|fluvoxam|isrs|ssri/],
  ["irsn", /venlafax|desvenlafax|duloxet|milnacip|irsn|snri/],
  ["noradrenergico", /atomoxet|reboxet|viloxaz|buprop|wellbutrin/],
  ["alfa2agonista", /clonidin|atensina|guanfacin|intuniv/],
  ["antipsicoticoSedativo", /quetiap|olanzap|clozap/],
  ["antipsicotico", /risperid|aripipraz|ziprasidon|haloperid|lurasidon|paliperid/],
  ["estabilizador", /valpro|divalpro|depak|lamotrig|lamictal|carbamaz|oxcarbaz|litio|carbonato de litio|topiram/],
  ["dopaminergico", /pramipex|ropinirol|amantad|levodopa|selegil/],
  ["gabapentinoide", /pregabal|gabapent/],
];

export function classifyMedication(input: RegimenMedicationInput): PharmClass {
  const text = norm(
    [
      input.substances?.name,
      input.substances?.pharmacological_class,
      input.substances?.clinical_category,
      input.brand_name,
      input.free_text_name,
      input.display_name,
    ]
      .filter(Boolean)
      .join(" "),
  );
  for (const [cls, pattern] of CLASS_PATTERNS) if (pattern.test(text)) return cls;
  return "outro";
}

const CLASS_LABEL: Record<PharmClass, string> = {
  estimulante: "Estimulante / pró-fármaco catecolaminérgico",
  benzodiazepinico: "Benzodiazepínico",
  opioide: "Opioide",
  isrs: "ISRS",
  irsn: "IRSN",
  moduladorSerotoninergico: "Modulador serotoninérgico",
  noradrenergico: "Noradrenérgico / dopaminérgico não estimulante",
  alfa2agonista: "Agonista alfa-2",
  antipsicoticoSedativo: "Antipsicótico sedativo",
  antipsicotico: "Antipsicótico",
  estabilizador: "Estabilizador de humor / anticonvulsivante",
  dopaminergico: "Agonista dopaminérgico",
  hipnotico: "Hipnótico",
  gabapentinoide: "Gabapentinoide",
  outro: "Classe não identificada",
};

export function pharmClassLabel(cls: PharmClass) {
  return CLASS_LABEL[cls];
}

function timeBucket(usualTime: any, frequency: any): RegimenItem["timeBucket"] {
  const raw = String(usualTime ?? "").slice(0, 5);
  const hour = /^\d{2}:\d{2}$/.test(raw) ? Number(raw.slice(0, 2)) : null;
  if (hour != null) {
    if (hour < 12) return "manha";
    if (hour < 18) return "tarde";
    return "noite";
  }
  const f = norm(frequency);
  if (/manha|matinal|cafe|acordar/.test(f)) return "manha";
  if (/tarde|almoco|almoço/.test(f)) return "tarde";
  if (/noite|deitar|dormir|jantar|noturn/.test(f)) return "noite";
  return "sem-horario";
}

function displayLabel(input: RegimenMedicationInput) {
  return (
    input.display_name ||
    input.brand_name ||
    input.substances?.name ||
    input.free_text_name ||
    "Medicação sem nome"
  );
}

function toNumber(value: any): number | null {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function buildItem(input: RegimenMedicationInput): RegimenItem {
  const status = String(input.status ?? "ativo");
  const dose = toNumber(input.current_dose);
  const gaps: string[] = [];
  if (!input.substance_id) gaps.push("sem substância canônica vinculada");
  if (!input.formulation_id) gaps.push("sem formulação PK/PD");
  if (dose == null) gaps.push("sem dose registrada");
  if (!input.frequency) gaps.push("sem frequência");
  if (!input.usual_time) gaps.push("sem horário usual");
  if (status === "planejado" && !input.clinical_rationale) gaps.push("planejado sem racional clínico");
  return {
    id: input.id,
    label: displayLabel(input),
    canonicalName: input.substances?.name ?? null,
    pharmClass: classifyMedication(input),
    status,
    isActive: status === "ativo",
    dose,
    doseUnit: input.dose_unit ?? null,
    frequency: input.frequency ?? null,
    usualTime: input.usual_time ? String(input.usual_time).slice(0, 5) : null,
    timeBucket: timeBucket(input.usual_time, input.frequency),
    gaps,
  };
}

const SEDATIVE_WEIGHT: Partial<Record<PharmClass, number>> = {
  benzodiazepinico: 35,
  opioide: 35,
  hipnotico: 30,
  antipsicoticoSedativo: 28,
  gabapentinoide: 20,
  alfa2agonista: 18,
  estabilizador: 12,
};

const SEROTONERGIC_WEIGHT: Partial<Record<PharmClass, number>> = {
  isrs: 35,
  irsn: 35,
  moduladorSerotoninergico: 30,
  opioide: 12,
  antipsicoticoSedativo: 8,
};

const CATECHOLAMINERGIC_WEIGHT: Partial<Record<PharmClass, number>> = {
  estimulante: 45,
  noradrenergico: 25,
  irsn: 18,
  dopaminergico: 20,
};

function buildLoad(
  axis: RegimenLoad["axis"],
  weights: Partial<Record<PharmClass, number>>,
  items: RegimenItem[],
): RegimenLoad {
  const contributors: string[] = [];
  let score = 0;
  for (const item of items) {
    const w = weights[item.pharmClass];
    if (!w) continue;
    score += w;
    contributors.push(item.label);
  }
  return { axis, score0_100: Math.min(100, score), contributors };
}

export function buildRegimenIntelligence(
  medications: RegimenMedicationInput[],
): RegimenIntelligence {
  const items = medications.map(buildItem);
  const activeItems = items.filter((item) => item.isActive);
  const findings: RegimenFinding[] = [];

  const byClass = new Map<PharmClass, RegimenItem[]>();
  for (const item of activeItems) {
    if (!byClass.has(item.pharmClass)) byClass.set(item.pharmClass, []);
    byClass.get(item.pharmClass)!.push(item);
  }
  const has = (cls: PharmClass) => byClass.get(cls) ?? [];

  // Duplicidade terapêutica dentro da mesma classe
  for (const [cls, group] of byClass) {
    if (cls === "outro" || group.length < 2) continue;
    findings.push({
      id: `dup-${cls}`,
      title: `Duplicidade de classe: ${CLASS_LABEL[cls].toLowerCase()}`,
      severity: cls === "benzodiazepinico" || cls === "opioide" ? "relevante" : "cautela",
      involved: group.map((g) => g.label),
      interpretation:
        "Dois ou mais tratamentos ativos compartilham a mesma classe. Pode ser intencional (titulação, troca em curso) ou soma involuntária de efeito e risco.",
      action: "Confirmar se é sobreposição planejada de troca ou duplicidade a ser resolvida.",
      monitor: ["efeito somado", "efeitos adversos de classe"],
    });
  }

  // Carga depressora do SNC
  const depressors = [...has("benzodiazepinico"), ...has("opioide"), ...has("hipnotico"), ...has("antipsicoticoSedativo"), ...has("gabapentinoide")];
  if (has("benzodiazepinico").length && has("opioide").length) {
    findings.push({
      id: "cns-benzo-opioide",
      title: "Benzodiazepínico + opioide ativos",
      severity: "critico",
      involved: [...has("benzodiazepinico"), ...has("opioide")].map((i) => i.label),
      interpretation: "Combinação com risco real de depressão respiratória, sobretudo se houver álcool ou aumento de dose.",
      action: "Revisar necessidade das duas classes, plano de desprescrição e orientação de sinais de alerta.",
      monitor: ["frequência respiratória", "nível de consciência", "saturação", "álcool"],
    });
  } else if (depressors.length >= 3) {
    findings.push({
      id: "cns-load",
      title: "Carga depressora do SNC somada",
      severity: "relevante",
      involved: depressors.map((i) => i.label),
      interpretation: "Três ou mais agentes sedativos ativos tendem a produzir sedação matinal, lentificação cognitiva e risco de queda.",
      action: "Concentrar sedativos à noite e avaliar retirada gradual do menos essencial.",
      monitor: ["sedação ao acordar", "cognição", "quedas"],
    });
  }

  // Soma serotoninergica
  const serotonergic = [...has("isrs"), ...has("irsn"), ...has("moduladorSerotoninergico")];
  if (serotonergic.length >= 2) {
    findings.push({
      id: "serotonin-load",
      title: "Soma serotoninérgica",
      severity: "cautela",
      involved: serotonergic.map((i) => i.label),
      interpretation: "Mais de um agente serotoninérgico ativo eleva risco de efeitos adversos serotoninérgicos e disfunção sexual/gastrointestinal.",
      action: "Verificar intenção clínica (potenciação vs troca) e checar sinais de excesso serotoninérgico.",
      monitor: ["tremor", "sudorese", "agitação", "clônus", "náusea"],
    });
  }

  // Estimulante + noradrenérgico
  if (has("estimulante").length && (has("noradrenergico").length || has("irsn").length)) {
    findings.push({
      id: "catecholamine-stack",
      title: "Soma catecolaminérgica",
      severity: "monitorar",
      involved: [...has("estimulante"), ...has("noradrenergico"), ...has("irsn")].map((i) => i.label),
      interpretation: "Cobertura executiva pode melhorar, mas pressão arterial, frequência cardíaca, ansiedade e insônia tendem a subir.",
      monitor: ["PA", "FC", "ansiedade", "sono"],
    });
  }

  // Estimulante manhã sem cobertura de tarde
  const stimulants = has("estimulante");
  if (stimulants.length && stimulants.every((s) => s.timeBucket === "manha")) {
    findings.push({
      id: "stimulant-afternoon-gap",
      title: "Possível janela executiva descoberta à tarde",
      severity: "info",
      involved: stimulants.map((i) => i.label),
      interpretation: "Todo o eixo estimulante está concentrado na manhã. Queda de efeito no fim do dia é um gatilho comum de redose não planejada.",
      action: "Registrar foco e irritabilidade no fim da tarde antes de mudar dose.",
      monitor: ["foco 16–20h", "irritabilidade", "redose"],
    });
  }

  // Estimulante + benzodiazepínico noturno
  if (stimulants.length && has("benzodiazepinico").some((b) => b.timeBucket === "noite")) {
    findings.push({
      id: "stimulant-night-benzo",
      title: "Estimulante de manhã com benzodiazepínico à noite",
      severity: "monitorar",
      involved: [...stimulants, ...has("benzodiazepinico")].map((i) => i.label),
      interpretation: "Ressaca sedativa matinal pode ser confundida com falha do estimulante e levar a aumento de dose desnecessário.",
      monitor: ["sedação ao acordar", "foco antes da dose"],
    });
  }

  // Planejado sem início
  const planned = items.filter((item) => item.status === "planejado");
  if (planned.length) {
    findings.push({
      id: "planned-not-started",
      title: "Tratamentos planejados ainda não iniciados",
      severity: "info",
      involved: planned.map((i) => i.label),
      interpretation: "Estes itens existem como plano e não devem entrar em análise de efeito atual nem em curvas.",
      action: "Definir gatilho e data para iniciar, ou marcar como descartado.",
    });
  }

  const loads = [
    buildLoad("sedativa", SEDATIVE_WEIGHT, activeItems),
    buildLoad("serotoninergica", SEROTONERGIC_WEIGHT, activeItems),
    buildLoad("catecolaminergica", CATECHOLAMINERGIC_WEIGHT, activeItems),
  ].filter((load) => load.score0_100 > 0);

  const schedule: RegimenIntelligence["schedule"] = { manha: [], tarde: [], noite: [], "sem-horario": [] };
  for (const item of activeItems) schedule[item.timeBucket].push(item);
  for (const bucket of Object.values(schedule)) bucket.sort((a, b) => (a.usualTime ?? "").localeCompare(b.usualTime ?? ""));

  const checks = activeItems.length * 5;
  const missing = activeItems.reduce((total, item) => total + Math.min(5, item.gaps.length), 0);
  const completeness0_100 = checks === 0 ? 0 : Math.round(((checks - missing) / checks) * 100);

  const dataGaps = Array.from(
    new Set(activeItems.flatMap((item) => item.gaps.map((gap) => `${item.label}: ${gap}`))),
  );

  const order: RegimenSeverity[] = ["critico", "relevante", "cautela", "monitorar", "info"];
  findings.sort((a, b) => order.indexOf(a.severity) - order.indexOf(b.severity));

  return { items, activeItems, findings, loads, schedule, completeness0_100, dataGaps };
}
